/**
 * EntityDocumentsPanel
 *
 * Displays the document library for an account or contact entity.
 * Shows validity status, upload date, expiry, "used in deals" count,
 * and action buttons (View, Edit, Delete).
 *
 * Props:
 *   entityId   — The UUID of the account or contact
 *   entityType — "account" | "contact"
 *
 * API:
 *   GET  /api/crm/accounts/:id/documents
 *   GET  /api/crm/contacts/:id/documents
 *   DELETE /api/crm/entity-documents/:id
 */

import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Eye, Edit, Trash2, Plus, Loader2, Upload } from "lucide-react";
import { format, parseISO, differenceInDays, isPast } from "date-fns";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { DocumentUploadModal } from "@/components/documents/document-upload-modal";

// ─── Types ───────────────────────────────────────────────────────────────────

export type ValidityStatus = "current" | "expiring" | "expired" | "superseded" | "no_expiry";
type FilterOption = "all" | "current" | "expiring" | "expired";

export interface EntityDocument {
  id: string;
  accountId: string | null;
  contactId: string | null;
  documentTypeId: string;
  documentTypeName: string;
  fileName: string;
  fileUrl: string | null;
  documentDate: string | null;
  validFrom: string | null;
  validUntil: string | null;
  isCurrent: boolean;
  uploadedAt: string;
  notes: string | null;
  /** Pre-computed by the API; used to determine badge colour */
  validityStatus: ValidityStatus;
  /** Number of deals this document is linked to */
  usedInDeals: number;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface EntityDocumentsPanelProps {
  entityId: string;
  entityType: "account" | "contact";
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Compute validity status client-side from validUntil + isCurrent. */
function computeValidityStatus(doc: EntityDocument): ValidityStatus {
  // API already provides validityStatus — use it if present
  if (doc.validityStatus) return doc.validityStatus;

  if (!doc.isCurrent) return "superseded";
  if (!doc.validUntil) return "no_expiry";

  const until = parseISO(doc.validUntil);
  if (isPast(until)) return "expired";
  if (differenceInDays(until, new Date()) <= 30) return "expiring";
  return "current";
}

function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  try {
    return format(parseISO(dateStr), "d MMM yyyy");
  } catch {
    return "-";
  }
}

// ─── Badge ────────────────────────────────────────────────────────────────────

function ValidityBadge({ status }: { status: ValidityStatus }) {
  if (status === "no_expiry") return <span className="text-muted-foreground text-sm">-</span>;

  const variantMap: Record<ValidityStatus, { className: string; label: string }> = {
    current: {
      className: "bg-green-100 text-green-800 border-green-200 hover:bg-green-100",
      label: "Current",
    },
    expiring: {
      className: "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100",
      label: "Expiring",
    },
    expired: {
      className: "bg-red-100 text-red-800 border-red-200 hover:bg-red-100",
      label: "Expired",
    },
    superseded: {
      className: "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-100",
      label: "Superseded",
    },
    no_expiry: { className: "", label: "" },
  };

  const config = variantMap[status];
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`validity-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function EntityDocumentsPanel({ entityId, entityType }: EntityDocumentsPanelProps) {
  const { toast } = useToast();
  const [docToDelete, setDocToDelete] = useState<EntityDocument | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterOption>("all");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<EntityDocument | null>(null);

  // Build query endpoint based on entity type
  const endpoint =
    entityType === "account"
      ? `/api/crm/accounts/${entityId}/documents`
      : `/api/crm/contacts/${entityId}/documents`;

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const { data: documents = [], isLoading } = useQuery<EntityDocument[]>({
    queryKey: [endpoint],
    enabled: !!entityId,
  });

  // ── Delete mutation ────────────────────────────────────────────────────────

  const deleteMutation = useMutation({
    mutationFn: (docId: string) =>
      apiRequest("DELETE", `/api/crm/entity-documents/${docId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoint] });
      toast({ title: "Document deleted", description: "The document has been removed." });
      setDocToDelete(null);
    },
    onError: () => {
      toast({
        title: "Delete failed",
        description: "Could not delete the document. Please try again.",
        variant: "destructive",
      });
    },
  });

  // ── Filter ─────────────────────────────────────────────────────────────────

  const filteredDocuments = documents.filter((doc) => {
    if (filterStatus === "all") return true;
    const status = computeValidityStatus(doc);
    return status === filterStatus;
  });

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleUploadClick() {
    setEditingDocument(null);
    setUploadModalOpen(true);
  }

  function handleViewClick(doc: EntityDocument) {
    if (doc.fileUrl) {
      window.open(doc.fileUrl, "_blank", "noopener,noreferrer");
    } else {
      toast({
        title: "No file available",
        description: "This document does not have a file attached.",
      });
    }
  }

  function handleEditClick(doc: EntityDocument) {
    setEditingDocument(doc);
    setUploadModalOpen(true);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Card data-testid="entity-documents-panel">
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base font-medium">Documents</CardTitle>
          <div className="flex items-center gap-2">
            {/* Filter select */}
            <Select
              value={filterStatus}
              onValueChange={(v) => setFilterStatus(v as FilterOption)}
            >
              <SelectTrigger
                className="h-8 w-36 text-sm"
                data-testid="documents-filter-select"
              >
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="current">Current</SelectItem>
                <SelectItem value="expiring">Expiring</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>

            {/* Upload button */}
            <Button
              size="sm"
              onClick={handleUploadClick}
              data-testid="upload-document-button"
            >
              <Plus className="h-4 w-4 mr-1" />
              Upload Document
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div
              className="flex items-center justify-center py-8"
              data-testid="documents-loading"
            >
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-3"
              data-testid="documents-empty-state"
            >
              <Upload className="h-10 w-10 opacity-30" />
              <p className="text-sm font-medium">No documents stored</p>
              <p className="text-xs">
                {filterStatus !== "all"
                  ? "Try changing the filter above."
                  : "Upload a document to get started."}
              </p>
              {filterStatus === "all" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUploadClick}
                  data-testid="empty-upload-document-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Upload Document
                </Button>
              )}
            </div>
          ) : (
            <Table data-testid="documents-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Document Type</TableHead>
                  <TableHead>Upload Date</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Used In</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.map((doc) => {
                  const status = computeValidityStatus(doc);
                  return (
                    <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                      <TableCell className="font-medium">
                        {doc.documentTypeName}
                      </TableCell>
                      <TableCell>{formatDateShort(doc.uploadedAt)}</TableCell>
                      <TableCell>{formatDateShort(doc.validUntil)}</TableCell>
                      <TableCell>
                        <ValidityBadge status={status} />
                      </TableCell>
                      <TableCell className="text-center" data-testid={`used-in-${doc.id}`}>
                        {doc.usedInDeals ?? 0}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewClick(doc)}
                            title="View document"
                            data-testid={`view-document-${doc.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(doc)}
                            title="Edit document"
                            data-testid={`edit-document-${doc.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDocToDelete(doc)}
                            title="Delete document"
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-document-${doc.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!docToDelete}
        onOpenChange={(open) => { if (!open) setDocToDelete(null); }}
      >
        <AlertDialogContent data-testid="delete-document-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{docToDelete?.documentTypeName}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleteMutation.isPending}
              data-testid="delete-document-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => docToDelete && deleteMutation.mutate(docToDelete.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-document-confirm"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Document upload / edit modal (F005) */}
      <DocumentUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        mode="entity"
        entityId={entityId}
        entityType={entityType}
        existingDocument={editingDocument as Record<string, unknown> | null}
      />
    </>
  );
}
